import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ArrowRight, MapPin, CalendarDays, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FadeUp, Eyebrow, EASE } from "../components/Reveal";

const STEPS = ["Service", "Location", "Date & Time", "Details", "Review"];

export default function Book() {
  const { user, register } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [serviceId, setServiceId] = useState(params.get("service") || null);
  const [locationId, setLocationId] = useState(params.get("location") || null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [cashDeposit, setCashDeposit] = useState(false);
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [acct, setAcct] = useState({ password: "", phone: "" });
  const [acctDone, setAcctDone] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/locations")]).then(([s, l]) => {
      setServices(s.data);
      setLocations(l.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setGuest({ name: user.name, email: user.email, phone: user.phone || "" });
  }, [user]);

  const service = services.find((s) => s.service_id === serviceId);
  const location = locations.find((l) => l.location_id === locationId);
  const price = useMemo(
    () => (service && location ? Math.max(0, service.base_price + (location.rate_adjustment || 0)) : null),
    [service, location]
  );
  const today = new Date().toISOString().split("T")[0];

  const canNext =
    (step === 0 && serviceId) ||
    (step === 1 && locationId) ||
    (step === 2 && date && time) ||
    (step === 3 && (user || (guest.name && guest.email))) ||
    step === 4;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { service_id: serviceId, location_id: locationId, date, time, notes, cash_deposit: cashDeposit };
      if (!user) Object.assign(payload, { guest_name: guest.name, guest_email: guest.email, guest_phone: guest.phone });
      const { data } = await api.post("/bookings", payload);
      setDone(data);
      toast.success("Booking request sent — pending Empress's confirmation");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const createAccount = async () => {
    try {
      await register({ name: guest.name, email: guest.email, password: acct.password, phone: acct.phone || guest.phone });
      setAcctDone(true);
      toast.success("Account created — your booking is now linked to your portal");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (done) {
    return (
      <div className="bg-lilac-mist min-h-screen py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-8">
          <FadeUp className="bg-white rounded-3xl border border-purple-100 p-10 text-center shadow-xl" data-testid="booking-success">
            <span className="w-16 h-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto">
              <Sparkles className="text-gold" size={26} />
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl mt-6">Request received</h1>
            <p className="text-slate-500 mt-4 text-sm leading-relaxed max-w-md mx-auto">
              Your booking is <span className="text-purple-700 font-semibold">pending</span>. No time is confirmed until Empress personally approves it — you'll get a text as soon as she does.
            </p>
            <div className="mt-8 bg-lilac-mist rounded-2xl p-6 text-left text-sm space-y-2">
              <p><span className="text-slate-400">Service:</span> {done.service_name}</p>
              <p><span className="text-slate-400">Location:</span> {done.location_name}</p>
              <p><span className="text-slate-400">Requested:</span> {done.date} at {done.time}</p>
              <p><span className="text-slate-400">Estimated rate:</span> <span className="font-semibold text-purple-700">${done.price}</span></p>
            </div>

            {!user && !acctDone && (
              <div className="mt-8 border border-dashed border-gold/50 rounded-2xl p-6 text-left" data-testid="create-account-prompt">
                <p className="font-serif text-xl">Track this booking in your own portal</p>
                <p className="text-sm text-slate-500 mt-2">Create a free account to manage bookings, listen to mixes and download your masters.</p>
                <div className="mt-4 grid gap-3">
                  <input type="password" placeholder="Choose a password" value={acct.password}
                    onChange={(e) => setAcct({ ...acct, password: e.target.value })}
                    data-testid="create-account-password"
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                  <input type="tel" placeholder="Phone (for text confirmations)" value={acct.phone}
                    onChange={(e) => setAcct({ ...acct, phone: e.target.value })}
                    data-testid="create-account-phone"
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                  <button onClick={createAccount} disabled={acct.password.length < 6} data-testid="create-account-submit"
                    className="px-6 py-3 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors disabled:opacity-40">
                    Create my account
                  </button>
                  <Link to="/" className="text-xs text-slate-400 hover:text-purple-700 text-center" data-testid="skip-account-link">Continue without an account</Link>
                </div>
              </div>
            )}
            {(user || acctDone) && (
              <Link to="/portal" data-testid="goto-portal-btn" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">
                Open my portal <ArrowRight size={15} />
              </Link>
            )}
          </FadeUp>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-lilac-mist min-h-screen py-20 lg:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <FadeUp className="text-center">
          <Eyebrow>Book the Studio</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight">Request your <span className="italic text-purple-700">session</span></h1>
          <p className="text-slate-500 text-sm mt-4">Empress reviews every request personally — nothing is confirmed until she approves it.</p>
        </FadeUp>

        <div className="flex items-center justify-center gap-2 mt-10" data-testid="booking-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <button onClick={() => i < step && setStep(i)} data-testid={`step-indicator-${i}`}
                className={`w-8 h-8 rounded-full text-xs font-mono flex items-center justify-center transition-colors ${
                  i < step ? "bg-purple-700 text-white" : i === step ? "bg-obsidian text-gold" : "bg-white text-slate-400 border border-purple-100"
                }`}>
                {i < step ? <Check size={13} /> : i + 1}
              </button>
              {i < STEPS.length - 1 && <span className={`w-6 sm:w-10 h-px ${i < step ? "bg-purple-700" : "bg-purple-100"}`} />}
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
          className="mt-8 bg-white rounded-3xl border border-purple-100 p-6 sm:p-10 shadow-sm" data-testid="booking-form">

          {step === 0 && (
            <div>
              <p className="font-serif text-2xl mb-6">What are we making?</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {services.map((s) => (
                  <button key={s.service_id} onClick={() => setServiceId(s.service_id)} data-testid={`book-service-${s.service_id}`}
                    className={`text-left rounded-2xl border p-5 transition-all ${serviceId === s.service_id ? "border-purple-600 bg-lilac-mist shadow-md" : "border-purple-100 hover:border-gold"}`}>
                    <p className="font-medium text-sm">{s.name}</p>
                    <p className="text-xs text-slate-400 mt-1">from ${s.base_price} {s.unit}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="font-serif text-2xl mb-6">Where does it happen?</p>
              <div className="grid gap-3">
                {locations.map((l) => (
                  <button key={l.location_id} onClick={() => setLocationId(l.location_id)} data-testid={`book-location-${l.location_id}`}
                    className={`text-left rounded-2xl border p-5 transition-all flex items-start gap-4 ${locationId === l.location_id ? "border-purple-600 bg-lilac-mist shadow-md" : "border-purple-100 hover:border-gold"}`}>
                    <MapPin size={18} className={locationId === l.location_id ? "text-purple-700 mt-0.5" : "text-slate-300 mt-0.5"} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{l.name}</p>
                      <p className="text-xs text-slate-400 mt-1">{l.description}</p>
                    </div>
                    <span className="text-xs font-mono text-slate-500 shrink-0">
                      {l.rate_adjustment === 0 ? "base rate" : l.rate_adjustment > 0 ? `+$${l.rate_adjustment}` : `-$${Math.abs(l.rate_adjustment)}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="font-serif text-2xl mb-6">When would you like it?</p>
              <p className="text-xs text-slate-400 mb-6">This is a request window — Empress confirms the final time.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-2"><CalendarDays size={12} /> Date</span>
                  <input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date-input"
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                </label>
                <label className="block">
                  <span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-2"><Clock size={12} /> Time</span>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="book-time-input"
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <p className="font-serif text-2xl mb-6">Tell me about the project</p>
              {!user && (
                <div className="grid gap-4 sm:grid-cols-2 mb-4">
                  <input placeholder="Your name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} data-testid="book-guest-name"
                    className="rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                  <input type="email" placeholder="Email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} data-testid="book-guest-email"
                    className="rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                  <input type="tel" placeholder="Phone (for text confirmation)" value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} data-testid="book-guest-phone"
                    className="rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400 sm:col-span-2" />
                </div>
              )}
              <textarea rows={5} placeholder="Song or session details, references, questions, links to demos…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="book-notes-input"
                className="w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
              <div className="mt-5 rounded-2xl border border-purple-100 bg-white p-5" data-testid="book-deposit-choice">
                <p className="text-sm font-medium text-obsidian">Deposit</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Once Empress approves, a {service?.deposit_percent ?? 50}% deposit (about <span className="font-semibold text-obsidian">${(price * (service?.deposit_percent ?? 50) / 100).toFixed(2)}</span>) locks your time. A card deposit is encouraged — it confirms instantly.</p>
                <label className="mt-4 flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={cashDeposit} onChange={(e) => setCashDeposit(e.target.checked)} data-testid="book-cash-checkbox" className="mt-0.5 accent-[#7C3AED] w-4 h-4" />
                  <span className="text-sm text-slate-700">
                    I'll bring my deposit in cash
                    <span className="block text-xs text-slate-500 mt-1 leading-relaxed">Only if you've arranged this with Empress. Your time is not locked until she confirms the cash was received — unconfirmed sessions can be released to other artists.</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="book-review">
              <p className="font-serif text-2xl mb-6">Review &amp; send</p>
              <div className="bg-lilac-mist rounded-2xl p-6 text-sm space-y-3">
                <p><span className="text-slate-400">Service:</span> {service?.name}</p>
                <p><span className="text-slate-400">Location:</span> {location?.name}</p>
                <p><span className="text-slate-400">Requested:</span> {date} at {time}</p>
                <p><span className="text-slate-400">For:</span> {user ? user.name : guest.name} ({user ? user.email : guest.email})</p>
                {notes && <p><span className="text-slate-400">Notes:</span> {notes}</p>}
                <p><span className="text-slate-400">Deposit:</span> {cashDeposit ? "Cash, arranged with Empress (locks after she confirms)" : `${service?.deposit_percent ?? 50}% by card after approval`}</p>
                <p className="pt-3 border-t border-purple-100">
                  <span className="text-slate-400">Estimated rate:</span>{" "}
                  <span className="font-serif text-2xl text-purple-700" data-testid="book-review-price">${price}</span>{" "}
                  <span className="text-xs text-slate-400">{service?.unit}</span>
                </p>
              </div>
              <p className="text-xs text-slate-400 mt-4">Submitting sends a request only. Empress approves, declines or proposes a new time — you receive a text the moment she decides.</p>
            </div>
          )}

          <div className="flex items-center justify-between mt-10">
            <button onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="booking-back-btn"
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-purple-700 disabled:opacity-0 transition-colors">
              <ArrowLeft size={15} /> Back
            </button>
            {step < 4 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="booking-next-btn"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors disabled:opacity-30">
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="booking-submit-btn"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-obsidian font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-50">
                {submitting ? "Sending…" : "Send request"} <ArrowRight size={15} />
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
